import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { IClub } from "@eagerdog/interfaces";
import { Config } from "@eagerdog/config";

import "./GoogleMapComponent.scss";

interface IProps {
  club: IClub;
  readonly?: boolean;
  onLocationChange?: (coordinates: { lat: number; lng: number }) => void;
}

// Declare global google types
declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

const GoogleMapComponent: React.FC<IProps> = ({ club, readonly = false, onLocationChange }) => {
  const [map, setMap] = useState<any>(null);
  const [marker, setMarker] = useState<any>(null);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceId = useRef<string>(Math.random().toString(36).substr(2, 9));

  // Default coordinates (center of US if no location provided)
  const defaultCoords = useMemo(() => ({ lat: 39.8283, lng: -98.5795 }), []);

  const getCoordinates = useCallback(async () => {
    // If club has coordinates, use them
    if (club.coordinates) {
      return club.coordinates;
    }

    // Try to geocode the address
    if (club.address && window.google) {
      const geocoder = new window.google.maps.Geocoder();
      const address = `${club.address.line1 || ''} ${club.address.city || ''} ${club.address.state || ''} ${club.address.postal_code || ''}`.trim();
      
      if (address) {
        try {
          const result = await new Promise((resolve, reject) => {
            geocoder.geocode({ address }, (results: any, status: any) => {
              if (status === 'OK' && results[0]) {
                resolve({
                  lat: results[0].geometry.location.lat(),
                  lng: results[0].geometry.location.lng()
                });
              } else {
                reject(new Error('Geocoding failed'));
              }
            });
          });
          return result;
        } catch (e) {
          console.warn('Geocoding failed, using default coordinates');
        }
      }
    }

    return defaultCoords;
  }, [club, defaultCoords]);

  const initializeMap = useCallback(async () => {
    if (!window.google || !window.google.maps) {
      setError("Google Maps failed to load");
      return;
    }

    // Prevent multiple initializations
    if (map) {
      return;
    }

    try {
      const coordinates = await getCoordinates();
      const mapElement = mapContainerRef.current;
      
      if (!mapElement) {
        return;
      }

      // Clear any existing content and ensure we have a clean container
      mapElement.innerHTML = '';
      
      // Wait a tick for React to finish any pending operations
      await new Promise(resolve => setTimeout(resolve, 0));
      
      const newMap = new window.google.maps.Map(mapElement, {
        center: coordinates,
        zoom: club.coordinates ? 15 : 10,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: !readonly,
        zoomControl: true,
        gestureHandling: readonly ? 'none' : 'auto'
      });

      const newMarker = new window.google.maps.Marker({
        position: coordinates,
        map: newMap,
        title: club.club_name,
        draggable: !readonly
      });

      // Add click listener for non-readonly mode
      if (!readonly && onLocationChange) {
        newMap.addListener('click', (e: any) => {
          const newCoords = {
            lat: e.latLng.lat(),
            lng: e.latLng.lng()
          };
          newMarker.setPosition(newCoords);
          onLocationChange(newCoords);
        });

        newMarker.addListener('dragend', (e: any) => {
          const newCoords = {
            lat: e.latLng.lat(),
            lng: e.latLng.lng()
          };
          onLocationChange(newCoords);
        });
      }

      setMap(newMap);
      setMarker(newMarker);
      setIsLoaded(true);
    } catch (e) {
      console.error('Map initialization failed:', e);
      setError("Failed to initialize map");
    }
  }, [club, readonly, getCoordinates, onLocationChange, map]);

  useEffect(() => {
    let mounted = true;
    
    const loadMap = async () => {
      // Check if API key is available
      const apiKey = Config.web.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        if (mounted) {
          setError("Google Maps API key not configured");
        }
        return;
      }
      
      // Check if Google Maps API is already loaded
      if (window.google && window.google.maps) {
        if (mounted) {
          initializeMap();
        }
        return;
      }

      // Check if script is already being loaded
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        const handleLoad = () => {
          if (mounted) {
            initializeMap();
          }
        };
        
        const handleError = () => {
          if (mounted) {
            setError("Failed to load Google Maps");
          }
        };
        
        existingScript.addEventListener('load', handleLoad);
        existingScript.addEventListener('error', handleError);
        
        return () => {
          existingScript.removeEventListener('load', handleLoad);
          existingScript.removeEventListener('error', handleError);
        };
      }

      // Load Google Maps API
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (mounted) {
          initializeMap();
        }
      };
      script.onerror = () => {
        if (mounted) {
          setError("Failed to load Google Maps API. Please check your connection and try again.");
        }
      };
      document.head.appendChild(script);
    };

    loadMap();
    
    return () => {
      mounted = false;
    };
  }, [initializeMap]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (map) {
        try {
          // Clear all listeners
          window.google?.maps?.event?.clearInstanceListeners?.(map);
          
          // Clear the map container completely
          if (mapContainerRef.current) {
            mapContainerRef.current.innerHTML = '';
          }
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [map]);

  // Additional cleanup on unmount
  useEffect(() => {
    return () => {
      // Force clear the container on component unmount
      if (mapContainerRef.current) {
        try {
          mapContainerRef.current.innerHTML = '';
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, []);

  // Update map when club address changes
  useEffect(() => {
    const updateMapLocation = async () => {
      if (map && marker && window.google) {
        try {
          const coordinates = await getCoordinates();
          map.setCenter(coordinates);
          marker.setPosition(coordinates);
          
          // Adjust zoom based on whether we have specific coordinates or geocoded address
          const zoom = club.coordinates ? 15 : 10;
          if (map.getZoom() !== zoom) {
            map.setZoom(zoom);
          }
        } catch (e) {
          console.warn('Failed to update map location:', e);
        }
      }
    };

    // Only update if we have a map initialized
    if (map && marker) {
      updateMapLocation();
    }
  }, [club.address, club.coordinates, map, marker, getCoordinates]);

  if (error) {
    return (
      <div className={`GoogleMapComponent ${readonly ? 'readonly' : 'editable'}`}>
        <div className="mapError">
          <div className="icon">🗺️</div>
          <div className="message">
            {error}
          </div>
          {!readonly && (
            <div className="fallback">
              <p>You can still add location information in your club settings.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`GoogleMapComponent ${readonly ? 'readonly' : 'editable'}`}>
      <div className="mapWrapper">
        <div 
          ref={mapContainerRef} 
          className="mapContainer"
          key={mapInstanceId.current}
          suppressHydrationWarning={true}
        />
        {!isLoaded && !error && (
          <div className="mapLoading">
            <div className="spinner"></div>
            <div>Loading map...</div>
          </div>
        )}
      </div>
      {!readonly && (
        <div className="mapInstructions">
          <p>Click on the map or drag the marker to set your club's location</p>
        </div>
      )}
    </div>
  );
};

export default GoogleMapComponent;